/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, DoCheck, ElementRef, Input, KeyValueChanges, KeyValueDiffer, KeyValueDiffers, Renderer} from '@angular/core';
/**
 * \@ngModule CommonModule
 * 
 * \@whatItDoes Update an HTML element styles.
 * 
 * \@howToUse 
 * ```
 * <some-element [ngStyle]="{'font-style': styleExp}">...</some-element>
 * 
 * <some-element [ngStyle]="{'max-width.px': widthExp}">...</some-element>
 * 
 * <some-element [ngStyle]="objExp">...</some-element>
 * ```
 * 
 * \@description 
 * 
 * The styles are updated according to the value of the expression evaluation:
 * - keys are style names with an optional `.<unit>` suffix (ie 'top.px', 'font-style.em'),
 * - values are the values assigned to those properties (expressed in the given unit).
 * 
 * \@stable
 */
export class NgStyle implements DoCheck {
private _ngStyle: {[key: string]: string};
private _differ: KeyValueDiffer<string, string|number>;
/**
 * @param {?} _differs
 * @param {?} _ngEl
 * @param {?} _renderer
 */
constructor(
private _differs: KeyValueDiffers,
private _ngEl: ElementRef,
private _renderer: Renderer) {}
/**
 * @param {?} v
 * @return {?}
 */
set ngStyle(v: {[key: string]: string}) {
    this._ngStyle = v;
    if (!this._differ && v) {
      this._differ = this._differs.find(v).create();
    }
  }
/**
 * @return {?}
 */
ngDoCheck() {
    if (this._differ) {
      const /** @type {?} */ changes = this._differ.diff(this._ngStyle);
      if (changes) {
        this._applyChanges(changes);
      }
    }
  }
/**
 * @param {?} changes
 * @return {?}
 */
private _applyChanges(changes: KeyValueChanges<string, string|number>): void {
    changes.forEachRemovedItem((record) => this._setStyle(record.key, null));
    changes.forEachAddedItem((record) => this._setStyle(record.key, record.currentValue));
    changes.forEachChangedItem((record) => this._setStyle(record.key, record.currentValue));
  }
/**
 * @param {?} nameAndUnit
 * @param {?} value
 * @return {?}
 */
private _setStyle(nameAndUnit: string, value: string|number|null|undefined): void {
    const [name, unit] = nameAndUnit.split('.');
    value = value != null && unit ? `${value}${unit}` : value;

    this._renderer.setElementStyle(this._ngEl.nativeElement, name, /** @type {?} */(( value as string)));
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: '[ngStyle]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: KeyValueDiffers, },
{type: ElementRef, },
{type: Renderer, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'ngStyle': [{ type: Input },],
};
}

function NgStyle_tsickle_Closure_declarations() {
/** @type {?} */
NgStyle.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgStyle.ctorParameters;
/** @type {?} */
NgStyle.propDecorators;
/** @type {?} */
NgStyle.prototype._ngStyle;
/** @type {?} */
NgStyle.prototype._differ;
/** @type {?} */
NgStyle.prototype._differs;
/** @type {?} */
NgStyle.prototype._ngEl;
/** @type {?} */
NgStyle.prototype._renderer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/common/src/directives/ng_style.ts